import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import config from './config';
import { useAvatar } from './AvatarContext';
import { BUILT_IN_AVATARS, readStoredAvatar, storeSelectedAvatar } from './avatarCatalog';
import { getStoredVoicePreferences, saveVoicePreferences } from './voicePreferences';

// First-login step on Home, shown before the guided-tour invite: the
// caregiver picks which assistant they want. Tapping a card swaps the live
// avatar beside this panel (the shell keeps it visible) and it introduces
// itself; Continue saves the choice to the user record.

const CHOSEN_KEY_PREFIX = 'vicaAvatarChosen:';

const chosenKey = (userId) => `${CHOSEN_KEY_PREFIX}${userId || 'anonymous'}`;

export const hasChosenAvatarLocally = (userId) => {
  try {
    return localStorage.getItem(chosenKey(userId)) === 'true';
  } catch {
    return false;
  }
};

export const markAvatarChosenLocally = (userId) => {
  try {
    localStorage.setItem(chosenKey(userId), 'true');
  } catch {
    // storage unavailable: the backend record still answers next time
  }
};

const hasWebGLSupport = () => {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
};

const VOICE_LABELS = { female: 'Female voice', male: 'Male voice' };

// Only avatars with their own assistant name are offered here, so the name on
// the card is the name the assistant uses. (Others stay in Settings.)
const PICKER_AVATARS = BUILT_IN_AVATARS.filter((avatar) => avatar.assistantName);

const WELCOME_TEXT =
  'Welcome to VICA! First, please choose the assistant you would like to talk with. Tap a picture to meet them.';

const introFor = (name) =>
  `Hi, I'm ${name}. If you would like me as your assistant, tap Continue. Or tap another picture to meet someone else.`;

// Applies a built-in avatar now: its voice first, so the reload uses it.
export const applyAvatar = (avatar) => {
  if (avatar.voiceGender) {
    saveVoicePreferences({
      gender: avatar.voiceGender,
      profileValue: getStoredVoicePreferences().profile.value,
    });
  }
  storeSelectedAvatar(avatar.id);
};

function AvatarPicker({ onDone }) {
  const { speakText, stopSpeaking, assistantName } = useAvatar();
  const [pickedId, setPickedId] = useState(
    () =>
      PICKER_AVATARS.find((avatar) => avatar.id === readStoredAvatar())?.id ||
      PICKER_AVATARS[0].id
  );
  const [previewsReady, setPreviewsReady] = useState(false);
  const [saving, setSaving] = useState(false);
  // Set by a tap; the introduction is spoken after the new avatar starts
  // loading (the avatar holds speech until it is ready).
  const introPendingRef = useRef(false);
  // True once the caregiver has tapped a card; the opening setup below must
  // not override that choice or talk over the introduction.
  const userPickedRef = useRef(false);

  const picked = PICKER_AVATARS.find((avatar) => avatar.id === pickedId) || PICKER_AVATARS[0];

  useEffect(() => {
    if (userPickedRef.current) {
      return undefined;
    }
    // Start from the highlighted avatar so the one beside the panel matches.
    if (readStoredAvatar() !== picked.id) {
      applyAvatar(picked);
    }
    stopSpeaking();
    const timer = setTimeout(() => {
      if (!userPickedRef.current) {
        speakText(WELCOME_TEXT);
      }
    }, 300);
    return () => clearTimeout(timer);
    // Runs once when the picker opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!introPendingRef.current) {
      return;
    }
    introPendingRef.current = false;
    speakText(introFor(assistantName));
  }, [pickedId, assistantName, speakText]);

  useEffect(() => {
    let canceled = false;
    if (!hasWebGLSupport()) {
      return undefined;
    }
    import('@google/model-viewer')
      .then(() => {
        if (!canceled) {
          setPreviewsReady(true);
        }
      })
      .catch((error) => console.error('Failed to load model-viewer:', error));
    return () => {
      canceled = true;
    };
  }, []);

  const handlePick = (avatar) => {
    stopSpeaking();
    userPickedRef.current = true;
    introPendingRef.current = true;
    setPickedId(avatar.id);
    applyAvatar(avatar);
  };

  const handleContinue = async () => {
    const userId = localStorage.getItem('userId');
    stopSpeaking();
    setSaving(true);
    applyAvatar(picked);
    markAvatarChosenLocally(userId);
    try {
      if (userId) {
        await axios.post(`${config.API_BASE_URL}/user-avatar/select`, {
          userId,
          selectedAvatar: picked.id,
        });
      }
    } catch (error) {
      // The choice is applied locally; Settings can save it again later.
      console.error('Failed to save the chosen avatar:', error);
    }
    setSaving(false);
    onDone();
  };

  return (
    <div
      className="absolute inset-0 z-[1300] flex items-center justify-center overflow-auto p-4 md:p-7"
      role="dialog"
      aria-modal="true"
      aria-label="Choose your assistant"
    >
      <div className="w-full max-w-[1150px] rounded-[24px] bg-white px-6 py-7 shadow-[0_24px_70px_rgba(0,52,80,0.4)] md:px-9">
        <h1 className="m-0 text-[2.2rem] font-extrabold text-[#0f2b3a]">Choose your assistant</h1>
        <p className="mt-2 mb-6 text-[1.35rem] leading-8 text-[#1c3f52]">
          Tap a picture to meet them. You can change this later in Settings.
        </p>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {PICKER_AVATARS.map((avatar) => {
            const isPicked = avatar.id === picked.id;
            return (
              <button
                key={avatar.id}
                type="button"
                onClick={() => handlePick(avatar)}
                aria-pressed={isPicked}
                className={`relative flex cursor-pointer flex-col items-center rounded-2xl border-[3px] bg-[#f7fbfe] p-3 text-center transition-all duration-150 ${
                  isPicked
                    ? 'border-[#006298] shadow-lg shadow-sky-100'
                    : 'border-transparent hover:border-sky-200'
                }`}
              >
                {isPicked && (
                  <CheckCircleIcon
                    sx={{ position: 'absolute', top: 10, right: 10, fontSize: 36, color: '#006298' }}
                  />
                )}
                {previewsReady ? (
                  <model-viewer
                    src={avatar.url}
                    alt={`${avatar.label} preview`}
                    camera-orbit="0deg 75deg 1m"
                    camera-target="0m 1.3m 0m"
                    style={{ width: '100%', height: '250px', display: 'block', pointerEvents: 'none' }}
                  />
                ) : (
                  <div className="flex h-[250px] w-full items-center justify-center rounded-xl bg-[#e3f1fa] text-[1.4rem] font-bold text-[#2c5b74]">
                    {avatar.label}
                  </div>
                )}
                <div className="mt-2 text-[1.6rem] font-extrabold text-[#0f3950]">{avatar.assistantName}</div>
                {VOICE_LABELS[avatar.voiceGender] && (
                  <div className="text-[1.15rem] font-semibold text-[#4d6f82]">
                    {VOICE_LABELS[avatar.voiceGender]}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            className="vica-btn vica-session-btn-new justify-center"
            onClick={handleContinue}
            disabled={saving}
          >
            {saving ? 'Saving...' : `Continue with ${picked.assistantName}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AvatarPicker;
